import React, { useState, useEffect } from "react";
import PageHeading from "../../components/PageHeading";
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  DingtalkCircleFilled,
} from "@ant-design/icons";
import axiosInstance from "../../utils/axios";
import { Button, Input, message, Popconfirm, Table, Tag } from "antd";
import { debounce } from "lodash";
import SectionModal from "./SectionModal";

const EventSections = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sections, setSections] = useState({
    current_page: 1,
    page_size: 20,
    results: [],
    count: 0
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || "");

  useEffect(() => {
    const delayFetch = debounce(() => {
      fetchData();
    }, 550);

    delayFetch();
    return () => delayFetch.cancel();
  }, [page, search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page,
        search: search
      });
      const response = await axiosInstance.get(`news_and_events/event-sections/?${params}`);
      setSections(response.data);
    } catch (error) {
      message.error("Failed to fetch event sections");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
    setSearchParams({ search: e.target.value });
  };

  const openModalUpdate = (id) => {
    navigate(`/admin/event-section-edit/${id}`);
  };

  const cancel = () => message.info("Cancelled");

  const confirmDelete = async (id) => {
    try {
      await axiosInstance.delete(`news_and_events/event-sections/${id}/`);
      message.success("Deleted successfully");
      fetchData();
    } catch (err) {
      message.error("Cannot delete");
    }
  };

  const groupPriceSummary = (groupPrices) => {
    if (!groupPrices || groupPrices.length === 0) return "No group pricing";
    const display = groupPrices.slice(0, 2).map(p => `${p.min_quantity}+: $${p.group_price}`).join(", ");
    return groupPrices.length > 2 ? `${display}...` : display;
  };

  const available = (record) => {
    return record.released - record.reserved;
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Event",
      dataIndex: ["event", "heading"],
      key: "event",
      render: (heading) => heading || "N/A"
    },
    {
      title: "Base Price",
      dataIndex: "price",
      key: "price",
      render: (price) => `$${parseFloat(price).toFixed(2)}`
    },
    {
      title: "Total",
      dataIndex: "count",
      key: "count",
    },
    {
      title: "Released",
      dataIndex: "released",
      key: "released",
    },
    {
      title: "Reserved",
      dataIndex: "reserved",
      key: "reserved",
    },
    {
      title: "Available",
      key: "available",
      render: (_, record) => available(record)
    },
    {
      title: "Group Prices",
      key: "group_prices",
      render: (_, record) => groupPriceSummary(record.group_prices)
    },
    {
      title: "Action",
      key: "actions",
      render: (_, record) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModalUpdate(record.id)}
          />
          <Popconfirm
            title="Delete"
            description="Are you sure to delete this section?"
            onConfirm={() => confirmDelete(record.id)}
            onCancel={cancel}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div className="dashboard">
      <PageHeading name="Event Sections" icon={<DingtalkCircleFilled />} />
      
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Search sections..."
          prefix={<SearchOutlined />}
          style={{ width: "190px" }}
          value={search}
          onChange={handleSearch}
        />
        <Button 
          onClick={() => navigate('/admin/events')}
          type="default"
        >
          ← Back to Events
        </Button>
      </div>

      <Table
        dataSource={sections.results || []}
        loading={loading}
        columns={columns}
        rowKey="id"
        scroll={{ x: "max-content" }}
        pagination={{
          current: page,
          pageSize: 20,
          total: sections.count,
          onChange: (newPage) => setPage(newPage),
        }}
      />
    </div>
  );
};

export default EventSections;

